<template>
  <ModalRoot v-model="isOpen">
    <template #body="{ close }">
      <ModalBody
        :is-open="isOpen"
        hide-close-button
        mobile-full-screen
        class="!p-0 !bg-bg w-full md:w-[500px] rounded-0 md:rounded-[35px]"
        @close="close"
      >
        <div
          class="p-6 bg-fill-brand-secondary backdrop-blur-[100px] flex flex-col gap-6"
        >
          <div class="h-10 justify-between items-center flex w-full">
            <h2 class="text-xl text-text font-serif">
              {{
                activeTab === LeaderboardLength.Monthly
                  ? $t("adminLeaderboard.monthlyPrize")
                  : $t("adminLeaderboard.weeklyPrize")
              }}
            </h2>
            <div
              class="w-10 h-10 bg-fill-brand-secondary rounded-full backdrop-blur-[35px] flex justify-center items-center cursor-pointer"
              @click="close"
            >
              <Icon icon="mdi:close" class="size-6 text-white" />
            </div>
          </div>

          <template v-if="!selectedPrize">
            <!-- Tabs -->
            <TabsRoot
              v-model="activeTab"
              :default-value="LeaderboardLength.Monthly"
              class="flex flex-col w-full"
            >
              <div class="flex justify-center relative mb-7">
                <TabsList
                  aria-label="leaderboard-tabs"
                  class="relative px-1 flex bg-brand-bgdivider-20 rounded-3xl text-base text-basics-primary font-serif w-full"
                >
                  <TabsIndicator
                    class="absolute py-1 left-0 h-full bottom-0 top-0 w-[--reka-tabs-indicator-size] translate-x-[--reka-tabs-indicator-position] transition-[width,transform] duration-300"
                  >
                    <div class="bg-brand-primary h-full rounded-2xl" />
                  </TabsIndicator>

                  <TabsTrigger
                    class="uppercase flex-1 text-sm h-full relative z-10 py-3 line-clamp-1 leading-none select-none outline-none cursor-pointer text-center"
                    :value="LeaderboardLength.Monthly"
                  >
                    {{ $t("adminLeaderboard.tabs.monthly") }}
                  </TabsTrigger>
                  <TabsTrigger
                    class="uppercase flex-1 text-sm h-full relative z-10 py-3 line-clamp-1 leading-none select-none outline-none cursor-pointer text-center"
                    :value="LeaderboardLength.Weekly"
                  >
                    {{ $t("adminLeaderboard.tabs.weekly") }}
                  </TabsTrigger>
                </TabsList>
              </div>

              <!-- Content -->
              <TabsContent :value="LeaderboardLength.Monthly">
                <Spinner v-if="loading" />
                <div v-else class="flex flex-col gap-2">
                  <LeaderboardEditItem
                    v-for="month in monthlyItems"
                    :key="month.id"
                    :date="toPrizeDate(month.start_date)"
                    :type="month.type"
                    :enabled="month.enabled"
                    @click="onItemClick(month)"
                  />
                </div>
              </TabsContent>

              <TabsContent :value="LeaderboardLength.Weekly">
                <Spinner v-if="loading" />
                <div v-else class="flex flex-col gap-2">
                  <LeaderboardEditItem
                    v-for="week in weeklyItems"
                    :key="week.id"
                    :date="toPrizeDate(week.start_date)"
                    :type="week.type"
                    :enabled="week.enabled"
                    @click="onItemClick(week)"
                  />
                </div>
              </TabsContent>
            </TabsRoot>
          </template>
          <template v-else>
            <LeaderboardPrizeDetails
              :date="toPrizeDate(selectedPrize.start_date)"
              :type="selectedPrize.type"
              :leaderboard-length="activeTab"
              :prize="selectedPrize"
              @save="onSave"
              @reset="reset"
            />
          </template>
        </div>
      </ModalBody>
    </template>
  </ModalRoot>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { Icon } from "@iconify/vue";
import ModalBody from "@/components/ui/modal/modal-body.vue";
import ModalRoot from "@/components/ui/modal/modal-root.vue";
import {
  TabsContent,
  TabsIndicator,
  TabsList,
  TabsRoot,
  TabsTrigger,
} from "reka-ui";
import { LeaderboardLength } from "@f3/shared/leaderboard-types";
import LeaderboardEditItem from "./leaderboard-edit-item.vue";
import LeaderboardPrizeDetails, {
  LeaderboardPrizeDetailsFragment,
} from "./leaderboard-prize-details.vue";
import { LeaderboardPrizeType } from "@/generated/graphql";
import { useQuery, useMutation } from "@vue/apollo-composable";
import { format } from "@/utils/date";
import type { UpdateLeaderboardPrizeInput } from "@/generated/graphql";
import { useToastStore } from "@/stores/toast.store";
import Spinner from "@/components/ui/spinner.vue";
import { useChannelStore } from "@/stores/channel.store";
import { graphql as gql } from "@/generated";
import { useI18n } from "vue-i18n";
import type { ResultOf } from "@graphql-typed-document-node/core";

type LeaderboardPrizeDetailsData = ResultOf<
  typeof LeaderboardPrizeDetailsFragment
>;
const GetLeaderboardPrizesDocument = gql(/* GraphQL */ `
  query GetLeaderboardPrizes(
    $leaderboard_length: LeaderboardLength
    $community_id: String
  ) {
    getLeaderboardPrizes(
      leaderboard_length: $leaderboard_length
      community_id: $community_id
    ) {
      ... on LeaderboardPrizeList {
        prizes {
          id
          title
          start_date
          type
          enabled
          ...LeaderboardPrizeDetailsData
        }
      }
    }
  }
`);

const UpdateLeaderboardPrizeDocument = gql(/* GraphQL */ `
  mutation UpdateLeaderboardPrize($data: UpdateLeaderboardPrizeInput!) {
    updateLeaderboardPrize(input: $data) {
      ... on LeaderboardPrize {
        id
        title
        type
        enabled
        leaderboard_prize_points {
          points
          from_position
          to_position
        }
      }
    }
  }
`);

const { t } = useI18n();
const isOpen = defineModel<boolean>("isOpen", { required: true });

const toastStore = useToastStore();
const channelStore = useChannelStore();

function toPrizeDate(value: string | null | undefined) {
  return value ? new Date(value) : new Date();
}

const activeTab = ref(LeaderboardLength.Monthly);
const selectedPrize = ref<LeaderboardPrizeDetailsData | null>(null);

const updateLeaderboardPrize = useMutation(UpdateLeaderboardPrizeDocument);

const {
  result: leaderboardPrizes,
  refetch: refetchLeaderboardPrizes,
  loading,
} = useQuery(
  GetLeaderboardPrizesDocument,
  computed(() => ({
    leaderboard_length: activeTab.value,
    community_id: channelStore.selectedCommunity,
  })),
  { enabled: computed(() => !!activeTab.value && !!isOpen.value) },
);

const monthlyItems = computed(() => {
  if (activeTab.value !== LeaderboardLength.Monthly) {
    return [];
  }

  const result = leaderboardPrizes.value?.getLeaderboardPrizes;
  const prizes = result && "prizes" in result ? result.prizes : [];
  return prizes.map((p) => p as LeaderboardPrizeDetailsData);
});

const weeklyItems = computed(() => {
  if (activeTab.value !== LeaderboardLength.Weekly) {
    return [];
  }

  const result = leaderboardPrizes.value?.getLeaderboardPrizes;
  const prizes = result && "prizes" in result ? result.prizes : [];
  return prizes.map((p) => p as LeaderboardPrizeDetailsData);
});

const onItemClick = (prize: LeaderboardPrizeDetailsData) => {
  selectedPrize.value = prize;
};

const onSave = async (data: UpdateLeaderboardPrizeInput) => {
  try {
    await updateLeaderboardPrize.mutate({ data });
    toastStore.addToast({
      message: t("adminLeaderboard.prizeSaved"),
      type: "success",
    });
    await refetchLeaderboardPrizes();
    channelStore.lastLeaderboardUpdate = Date.now();
    reset();
  } catch (error) {
    console.error(error);
  }
};

const reset = () => {
  selectedPrize.value = null;
};

const _formatTitle = (type: LeaderboardPrizeType, start_date: Date) => {
  if (!type) {
    return "";
  }

  if (type === LeaderboardPrizeType.Monthly) {
    return format(new Date(start_date), "MMMM, yyyy");
  }

  return format(new Date(start_date), "MMMM d, yyyy");
};
</script>
